import { ProposalLetterhead, ProposalDocumentFooter } from "./ProposalBranding";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileText, ShieldCheck, AlertTriangle, Users, Camera, ClipboardList, BarChart3, Target, CheckCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import RiskBadge, { type RiskLevel } from "./RiskBadge";
import { Pencil, Check, X } from "lucide-react";

export interface AIResults {
  riskLevel: RiskLevel;
  executiveSummary: string;
  threatAssessment: string;
  riskImpact: string;
  staffingRecommendation: string;
  securityActions: string;
  implementationPlan: string;
  expectedOutcomes: string;
  crimeDataSummary?: string;
  // Legacy fields for backwards compat
  securityMeasures?: string;
  proposalSummary?: string;
  postOrders?: string;
}

interface ProposalResultsProps {
  results: AIResults | null;
  isLoading: boolean;
  onResultsChange?: (results: AIResults) => void;
}

const sections = [
  { key: "executiveSummary" as const, title: "A. Executive Summary", icon: FileText, label: "A" },
  { key: "threatAssessment" as const, title: "B. Threat Assessment", icon: ShieldCheck, label: "B" },
  { key: "riskImpact" as const, title: "C. Risk Level & Operational Impact", icon: AlertTriangle, label: "C" },
  { key: "staffingRecommendation" as const, title: "D. Recommended Security Staffing", icon: Users, label: "D" },
  { key: "securityActions" as const, title: "E. Recommended Security Actions", icon: Camera, label: "E" },
  { key: "crimeDataSummary" as const, title: "F. Crime Data Analysis", icon: BarChart3, label: "F" },
  { key: "implementationPlan" as const, title: "G. Implementation Plan", icon: Target, label: "G" },
  { key: "expectedOutcomes" as const, title: "H. Expected Outcomes", icon: CheckCircle, label: "H" },
];

const ProposalResults = ({ results, isLoading, onResultsChange }: ProposalResultsProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-lg font-medium">Generating Security Proposal...</p>
        <p className="text-sm mt-1">Analyzing crime data and generating recommendations</p>
      </div>
    );
  }

  if (!results) return null;

  const startEdit = (key: string, value: string) => {
    setEditingKey(key);
    setEditValue(value || "");
  };

  const saveEdit = () => {
    if (editingKey && results && onResultsChange) {
      onResultsChange({ ...results, [editingKey]: editValue });
    }
    setEditingKey(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue("");
  };

  return (
    <div id="security-proposal-content" className="proposal-document animate-fade-in">
      <ProposalLetterhead title="Security Proposal" />
      <div className="mb-6">
        <RiskBadge level={results.riskLevel} size="lg" />
      </div>

      {sections.map(({ key, title, icon: Icon }) => {
        const content = results[key];
        if (!content && key === "crimeDataSummary") return null;

        return (
          <Card key={key} data-pdf-block className="proposal-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-start justify-between gap-2 text-foreground text-sm">
                <span className="flex items-start gap-2 min-w-0">
                  <Icon className="h-4 w-4 shrink-0 text-primary" />
                  {title}
                </span>
                {onResultsChange && editingKey !== key && (
                  <Button
                    data-html2canvas-ignore
                    title={`Edit ${title}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(key, content || "")}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingKey === key ? (
                <div className="space-y-3">
                  <Textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="min-h-[120px] bg-secondary border-border text-foreground"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="gradient-primary text-primary-foreground">
                      <Check className="h-4 w-4 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>
                      <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={`prose prose-invert prose-sm max-w-none text-secondary-foreground whitespace-pre-wrap ${key === "staffingRecommendation" ? "font-mono text-xs" : ""}`}>
                  {content || "No data available for this section."}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      <ProposalDocumentFooter />
    </div>
  );
};

export default ProposalResults;
